import React, { useEffect, useState } from 'react';
import { Input, Modal } from 'antd';
import classNames from 'classnames';
import classnames from 'classnames';
import { CancelIcon, PencilIcon } from 'components/assets/Icons/Icons';
import { Button } from 'ui-components/button/button';
import styles from './conversation.module.scss';
import './modal-antd-overrides.css';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import { useSelector } from 'react-redux';
import {
    selectOfflineReportsRequestsCommentsLoading,
    selectOfflineReportsRequestsComments,
} from 'store/offline-reports/selectors/offline-reports-selectors-factory';
import { Comment, OrderManagementRequest } from 'core/entities';
import moment from 'moment';
import { CopyToClipboard } from 'components/copy-to-clipboard/copy-to-clipboard';
import { ReportTypeComponent } from 'features/offline-reports/order-management/order-management';
import { Loading } from 'ui-components/loading/loading-component';
import { dateFormats, getSimpleFormatDate } from 'utils/date/date';
import { ReportMaterialsComponent } from 'features/offline-reports/order-management/components/report-materials/report-materials';

interface ItemProps {
    title: string;
    content: React.ReactNode;
}

const RANGE_DATE_FORMAT = 'MMM DD, YYYY';
const REQUESTED_ON_DATE_FORMAT = 'MMM DD';

const Item = ({ title, content }: ItemProps) => {
    return (
        <div className={styles.orderDates}>
            <p>{title}</p>
            <div>{content}</div>
        </div>
    );
};

interface ConversationHeadProps {
    reportObject: NonNullable<ConversationProps['reportObject']>;
}

const ConversationHead = ({ reportObject }: ConversationHeadProps) => {
    const { reportDates, requestedOn } = reportObject;
    return (
        <div className={classnames(styles.conversationHead, styles.header)}>
            <div className={styles.headerDetails}>
                <p>Report Type & Format</p>
                <div>
                    <ReportTypeComponent isConversation record={reportObject} />
                </div>
            </div>
            <div className={styles.headerDates}>
                <Item
                    title="Report Dates"
                    content={
                        <p className={styles.date}>
                            {`${moment(reportDates.from).format(
                                REQUESTED_ON_DATE_FORMAT,
                            )} - ${moment(reportDates.to).format(RANGE_DATE_FORMAT)}`}
                        </p>
                    }
                />
                <Item
                    title="Requested On"
                    content={
                        <p className={styles.date}>
                            {moment(requestedOn).format(RANGE_DATE_FORMAT)}
                        </p>
                    }
                />
            </div>
        </div>
    );
};

interface ConversationViewProps {
    createdTime: string;
    authorName: string;
    ticketId: number;
    ticketStatus: string;
}

const ConversationDetailsView = ({
    authorName,
    ticketId,
    createdTime,
    ticketStatus,
}: ConversationViewProps) => {
    return (
        <>
            <div className={styles.ticketId}>
                <span className={styles.field}>Placer.ai support ticket #:</span>
                <CopyToClipboard copyValue={ticketId.toString()}>
                    <span>{ticketId}</span>
                </CopyToClipboard>
            </div>
            <div className={styles.commentHeader}>
                <p className={styles.author}>
                    <span className={styles.field}>Report ordered by:</span>
                    <span className={styles.value}>{authorName}</span>
                </p>
            </div>
            <div className={styles.reportStatus}>
                <p>
                    <span className={styles.field}>Report Status:</span>
                    <span className={styles.value}>
                        {ticketStatus === 'new' ? 'In progress' : ticketStatus}
                    </span>
                </p>
            </div>
        </>
    );
};

const ConversationCommentsView = ({ authorName, createdTime }: ConversationViewProps) => {
    return (
        <div className={styles.commentHeader}>
            <p className={styles.author}>
                <b className={styles.value}>{authorName}</b>
            </p>
            <p className={styles.date}>{createdTime}</p>
        </div>
    );
};

interface ConversationBodyProps {
    isLoading: boolean;
    comments: Comment[];
    ticketId: number;
    ticketStatus: string;
}

const ConversationBody = ({
    comments,
    isLoading,
    ticketId,
    ticketStatus,
}: ConversationBodyProps) => {
    return (
        <>
            <div className={classnames(styles.conversationBody, styles.body)}>
                <Loading className={styles.commentsLoading} show={isLoading}>
                    <div className={styles.detailsTitle}>
                        Here are the details of the ordered report:
                    </div>
                    {comments.map((comment, ind) => {
                        const authorName = comment.author_meta.name;
                        const createdTime = getSimpleFormatDate(
                            comment.created_at,
                            dateFormats.fullDateHour,
                        );
                        return (
                            <div key={ind} className={styles.commentContainer}>
                                <div className={styles.commentWrap}>
                                    {ind === 0 ? (
                                        <ConversationDetailsView
                                            createdTime={createdTime}
                                            ticketId={ticketId}
                                            ticketStatus={ticketStatus}
                                            authorName={authorName}
                                        />
                                    ) : (
                                        <ConversationCommentsView
                                            createdTime={createdTime}
                                            ticketId={ticketId}
                                            ticketStatus={ticketStatus}
                                            authorName={authorName}
                                        />
                                    )}
                                    <div
                                        className={classNames(
                                            styles.bodyTextWrapper,
                                            ind !== 0 && styles.bodyComments,
                                        )}
                                    >
                                        <p
                                            className={styles.bodyText}
                                            dangerouslySetInnerHTML={{ __html: comment.html_body }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Loading>
            </div>
            <div style={{ height: 10 }} />
        </>
    );
};

interface ConversationFooterProps {
    requestId: string;
    setRequestId: ConversationProps['setRequestId'];
    isLoading: boolean;
    reportObject: NonNullable<OrderManagementRequest>;
}

const ConversationFooter = ({
    requestId,
    setRequestId,
    isLoading,
    reportObject,
}: ConversationFooterProps) => {
    const [isExpanded, setIsExapnded] = useState<boolean>(false);
    const [comment, setComment] = useState<string>('');
    const { postOfflineReportsRequestsComment } = useOfflineReportsActions();

    const post = async () => {
        await postOfflineReportsRequestsComment({
            requestId,
            payload: { comment },
        });
        setIsExapnded(false);
        setComment('');
    };

    const isButtonDisabled = (isExpanded && comment.length === 0) || isLoading;
    const reportMaterialButtonTexts = {
        viewReport: 'View report',
        download: 'Download',
    };
    return (
        <div
            className={classnames(styles.footer, styles.conversationFooter, {
                [styles.openedFooter]: isExpanded,
            })}
        >
            {isExpanded && (
                <div className={classnames(styles.expandedFooter, styles.expandedFooterOpened)}>
                    <h3 className={styles.title}>Reply a message on this thread</h3>
                    <Input.TextArea
                        rows={6}
                        value={comment}
                        placeholder="Write your message here"
                        onChange={(e) => setComment(e.target.value)}
                    />
                </div>
            )}
            <div className={styles.buttonsWrap}>
                {isExpanded && (
                    <Button
                        className={styles.cancelButton}
                        onClick={() => {
                            setComment('');
                            setIsExapnded(false);
                        }}
                    >
                        Cancel
                    </Button>
                )}
                {!isExpanded && (
                    <ReportMaterialsComponent
                        record={reportObject}
                        viewReportButtonText={reportMaterialButtonTexts.viewReport}
                        downloadButtonText={reportMaterialButtonTexts.download}
                    />
                )}
                <Button
                    type={'brand-primary-full'}
                    onClick={() => (isExpanded ? post() : setIsExapnded(true))}
                    icon={<PencilIcon />}
                    disabled={isButtonDisabled}
                >
                    Send a Message
                </Button>
            </div>
        </div>
    );
};

interface ConversationProps {
    requestId: string;
    reportObject: OrderManagementRequest | null;
    tabsData?: Array<{
        key: string;
        reportType: React.ReactNode;
        reportDates: string;
        requestDate: string;
    }>;
    notSendRead?: boolean;
    setRequestId: (requestId: string | null) => void;
    setIsConversationOpen: (flag: boolean) => void;
}

export const Conversation = ({
    requestId,
    reportObject,
    notSendRead,
    setRequestId,
    setIsConversationOpen,
}: ConversationProps) => {
    const modalWrapClasses = classNames('modal-antd-overrides');

    const {
        getOfflineReportsRequestsComments,
        clearOfflineReportsRequestComments,
        setOfflineReportsRequestAsRead,
    } = useOfflineReportsActions();
    const comments = useSelector(selectOfflineReportsRequestsComments);
    const isLoading = useSelector(selectOfflineReportsRequestsCommentsLoading);

    useEffect(() => {
        getOfflineReportsRequestsComments(requestId);
        !notSendRead && setOfflineReportsRequestAsRead(requestId);
    }, [requestId, getOfflineReportsRequestsComments, setOfflineReportsRequestAsRead, notSendRead]);

    const onCloseModal = () => {
        setIsConversationOpen(false);
        setRequestId(null);
        clearOfflineReportsRequestComments();
    };

    if (!reportObject) {
        return null;
    }

    return (
        <div className={modalWrapClasses}>
            <Modal
                className={styles.conversationModal}
                visible={true}
                width="60%"
                footer={null}
                onCancel={onCloseModal}
                closeIcon={<CancelIcon />}
                bodyStyle={{ padding: 0 }}
            >
                <div
                    className={styles.conversationContainer}
                    data-testid="report-materials-conversation"
                >
                    <ConversationHead reportObject={reportObject} />

                    <ConversationBody
                        ticketId={reportObject.id}
                        ticketStatus={reportObject.status}
                        comments={comments}
                        isLoading={isLoading}
                    />

                    {reportObject.status !== 'closed' && (
                        <ConversationFooter
                            requestId={requestId}
                            isLoading={isLoading}
                            setRequestId={setRequestId}
                            reportObject={reportObject}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
};
